import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ROLES, ROLE_HINTS, ROLE_LABELS } from "@speisekarte/core";
import type { Role } from "@speisekarte/core";
import { team, untilDay } from "../account.ts";
import type { Invite, TeamMember } from "../account.ts";
import { messageOf, store, useStore } from "../store.ts";

const shortDay = (at: number) => new Date(at).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });

type LinkBox = { title: string; url: string; expiresAt: number; share: string; name: string };

/** "Betrieb" in the account page, as in KellerLog: who belongs to it; the owner invites, renames and manages. */
export function Team() {
  useStore();
  const [data, setData] = useState<{ role: Role; members: TeamMember[]; invites: Invite[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [box, setBox] = useState<LinkBox | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  async function load() {
    try {
      setData(await team.members());
    } catch (err) {
      setError(messageOf(err));
    }
  }
  useEffect(() => void load(), []);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err));
    }
    await load();
  }

  const inviteBox = (name: string, url: string, expiresAt: number): LinkBox => ({
    title: `Einladung für ${name}`,
    url,
    expiresAt,
    name,
    share: `Hallo ${name}, hier ist deine Einladung in den Betrieb „${store.workspaceName}“ (SpeiseKarte). Mit dem Link legst du dein Konto an; er gilt bis ${untilDay(expiresAt)}, und nur einmal.`,
  });

  if (!data) return <section className="card">{error ? <p className="error-box">{error}</p> : <p className="muted">Lädt …</p>}</section>;
  const owner = data.role === "owner";

  return (
    <section className="card team">
      <h2>Betrieb</h2>
      {owner ? <Rename /> : <p>{store.workspaceName}</p>}
      <ul className="list">
        {data.members.map((m) => (
          <li key={m.id} className="member" data-testid={`member-${m.displayName}`}>
            <div className="row between">
              <div className="grow">
                <strong>{m.displayName}</strong> {m.me && <span className="muted">(du)</span>}
                {owner && (
                  <div className="muted small">
                    {m.username}
                    {!m.hasEmail && " · ohne bestätigte E-Mail"}
                    {m.reset && <span className={m.reset.expiresAt <= Date.now() ? "error" : ""}> · Passwort zurückgesetzt, Link bis {shortDay(m.reset.expiresAt)}</span>}
                  </div>
                )}
              </div>
              <span className="side">{ROLE_LABELS[m.role]}</span>
            </div>
            {editing === m.id && (
              <RoleForm
                initial={m.role}
                onCancel={() => setEditing(null)}
                onSave={async (role) => {
                  await team.setRole(m.id, role);
                  setEditing(null);
                  await load();
                }}
              />
            )}
            {owner && !m.me && editing !== m.id && (
              <div className="actions">
                <button onClick={() => setEditing(m.id)}>Rolle</button>
                <button
                  onClick={() =>
                    act(async () => {
                      const passkeys = m.passkeys ? ` Auch die Passkeys von ${m.displayName} gelten erst wieder, wenn ein neues Passwort gesetzt ist; dabei lassen sie sich entfernen.` : "";
                      if (!confirm(`Passwort von ${m.displayName} zurücksetzen? Das bisherige gilt sofort nicht mehr, und ${m.displayName} wird auf allen Geräten abgemeldet.${passkeys}`)) return;
                      const { url, expiresAt } = await team.resetLink(m.id);
                      setBox({ title: `Neues Passwort für ${m.displayName}`, url, expiresAt, name: m.displayName, share: `Hallo ${m.displayName}, mit diesem Link legst du ein neues Passwort fest (SpeiseKarte). Er gilt bis ${untilDay(expiresAt)}, und nur einmal.` });
                    })
                  }
                >
                  Passwort zurücksetzen
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    if (confirm(`${m.displayName} aus dem Betrieb nehmen? Die Speisekarten, die ${m.displayName} gemacht hat, bleiben.`)) void act(() => team.remove(m.id));
                  }}
                >
                  Entfernen
                </button>
              </div>
            )}
          </li>
        ))}
        {data.invites.map((i) => (
          <li key={i.id} className="member" data-testid={`invite-${i.name}`}>
            <div className="row between">
              <div className="grow">
                <strong>{i.name}</strong>
                <div className={`small ${i.expired ? "error" : "muted"}`}>eingeladen, {i.expired ? "Link abgelaufen" : `Link bis ${shortDay(i.expiresAt)}`}</div>
              </div>
              <span className="side">{ROLE_LABELS[i.role]}</span>
            </div>
            {editing === i.id ? (
              <RoleForm
                initial={i.role}
                onCancel={() => setEditing(null)}
                onSave={async (role) => {
                  await team.setInviteRole(i.id, role);
                  setEditing(null);
                  await load();
                }}
              />
            ) : (
              <div className="actions">
                <button onClick={() => setEditing(i.id)}>Rolle</button>
                <button
                  onClick={() =>
                    act(async () => {
                      const { invite, url } = await team.renew(i.id);
                      setBox(inviteBox(invite.name, url, invite.expiresAt));
                    })
                  }
                >
                  Neuer Link
                </button>
                <button className="danger" onClick={() => act(() => team.withdraw(i.id))}>
                  Zurückziehen
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {box && <Box box={box} onClose={() => setBox(null)} />}
      {owner && !box && (
        <>
          {inviting ? (
            <InviteForm
              onCancel={() => setInviting(false)}
              onDone={async (name, role) => {
                const { invite, url } = await team.invite(name, role);
                setInviting(false);
                setBox(inviteBox(invite.name, url, invite.expiresAt));
                await load();
              }}
            />
          ) : (
            <button className="primary" onClick={() => setInviting(true)} data-testid="invite">
              Jemanden einladen
            </button>
          )}
          <p className="hint">Wer eingeladen ist, wählt Benutzername und Passwort selbst; das Passwort siehst du nie.</p>
        </>
      )}
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}

/** The roles as a list of full-width choices, each with what it may. */
function RoleChoice({ role, onChange }: { role: Role; onChange: (r: Role) => void }) {
  return (
    <fieldset className="choice vertical">
      <legend>Rolle</legend>
      {ROLES.map((r) => (
        <label key={r} className={role === r ? "on" : ""}>
          <input type="radio" name="role" checked={role === r} onChange={() => onChange(r)} data-testid={`role-${r}`} />
          <span>
            {ROLE_LABELS[r]}
            <span className="hint">{ROLE_HINTS[r]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function RoleForm({ initial, onSave, onCancel }: { initial: Role; onSave: (role: Role) => Promise<void>; onCancel: () => void }) {
  const [role, setRole] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave(role);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form noValidate onSubmit={submit} className="access-form">
      <RoleChoice role={role} onChange={setRole} />
      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <button className="primary" disabled={busy}>
          Speichern
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function InviteForm({ onDone, onCancel }: { onDone: (name: string, role: Role) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("staff");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onDone(name.trim(), role);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus data-testid="invite-name" />
        <span className="hint">So heißt die Person bei euch; sie kann den Namen ändern.</span>
      </label>
      <RoleChoice role={role} onChange={setRole} />
      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <button className="primary" disabled={busy || !name.trim()} data-testid="invite-create">
          Einladungslink erzeugen
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Box({ box, onClose }: { box: LinkBox; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(box.url).catch(() => undefined);
    setCopied(true);
  };
  return (
    <div className="linkbox" data-testid="link-box">
      <strong>{box.title}</strong>
      <input readOnly value={box.url} onFocus={(e) => e.target.select()} data-testid="link-url" />
      <div className="buttons">
        <button
          className="primary"
          onClick={async () => {
            if (navigator.share) await navigator.share({ text: `${box.share}\n${box.url}` }).catch(() => undefined);
            else await copy();
          }}
        >
          Link senden
        </button>
        <button onClick={copy}>{copied ? "Link kopiert" : "Link kopieren"}</button>
        <button className="link" onClick={onClose}>
          Fertig
        </button>
      </div>
      <p className="hint">
        Einmal verwendbar, gültig bis {untilDay(box.expiresAt)}. Schick ihn nur an {box.name}.
      </p>
    </div>
  );
}

function Rename() {
  const current = store.workspaceName;
  const [name, setName] = useState(current);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const { name: saved } = await team.rename(name);
      store.renamed(saved);
      setState({ ok: "Umbenannt" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Name
        <span className="row">
          <input value={name} onChange={(e) => [setName(e.target.value), setState({})]} maxLength={60} />
          <button className="primary" disabled={name.trim() === current || !name.trim()}>Umbenennen</button>
        </span>
      </label>
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error-box">{state.error}</p>}
    </form>
  );
}
